import { useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import {
  getInitialPageSize,
  getPageOnBySource,
  isValidPageSize,
  PaginationSource,
} from './use-pagination'
import { setLocalStorageValue } from 'shared/utils'

export const ROWS_PER_PAGE_OPTIONS = [5, 10, 20, 50]
export const DEFAULT_PAGE_SIZE = 10

const cacheKey = (source: PaginationSource) => `Pagination_OnPage_${source}`

const QUERY_PARAMS = {
  PAGE: 'page',
  START_CURSOR: 'startCursor',
  END_CURSOR: 'endCursor',
  ROWS_PER_PAGE: 'rowsPerPage',
} as const

export { QUERY_PARAMS as DATA_GRID_QUERY_PARAMS }

export type PaginationCursor = {
  before?: string
  after?: string
}

const getRowsPerPageFromSearch = (
  search: string,
  pageSizeOptions: readonly number[]
) => {
  const value = new URLSearchParams(search).get(QUERY_PARAMS.ROWS_PER_PAGE)

  const pageSize = value !== null ? Number(value) : undefined

  return isValidPageSize(pageSize, pageSizeOptions) ? pageSize : undefined
}

export const getInitialDataGridPageSize = ({
  search,
  source,
  pageSizeOptions,
  fallback,
}: {
  search: string
  source: PaginationSource
  pageSizeOptions: readonly number[]
  fallback: number
}) => {
  const rowsPerPage = new URLSearchParams(search).get(
    QUERY_PARAMS.ROWS_PER_PAGE
  )

  return getInitialPageSize({
    pageSizeFromUrl: rowsPerPage !== null ? Number(rowsPerPage) : undefined,
    cachedPageSize: getPageOnBySource(source),
    pageSizeOptions,
    fallback,
  })
}

const getPaginationFromSearch = (
  search: string,
  fallbackPage = 0
): {
  page: number
  cursor: PaginationCursor
} => {
  const params = new URLSearchParams(search)
  const pageParam = params.get(QUERY_PARAMS.PAGE)
  const startCursorParam = params.get(QUERY_PARAMS.START_CURSOR)
  const endCursorParam = params.get(QUERY_PARAMS.END_CURSOR)

  const validFallbackPage = Number.isFinite(fallbackPage)
    ? Math.max(0, fallbackPage)
    : 0

  const parsedPage = pageParam ? parseInt(pageParam, 10) - 1 : validFallbackPage

  const page = Number.isNaN(parsedPage)
    ? validFallbackPage
    : Math.max(0, parsedPage)

  if (page > 0 && startCursorParam) {
    return {
      page,
      cursor: { after: startCursorParam },
    }
  }

  if (page > 0 && endCursorParam) {
    return {
      page,
      cursor: { before: endCursorParam },
    }
  }

  return {
    page,
    cursor: {},
  }
}

const cursorsEqual = (
  currentCursor: PaginationCursor,
  nextCursor: PaginationCursor
) =>
  currentCursor.after === nextCursor.after &&
  currentCursor.before === nextCursor.before

export const useDataGridPagination2 = (
  loading: boolean,
  totalCount: number | undefined,
  pageInfo: {
    endCursor?: string | null
    startCursor?: string | null
    hasNextPage?: boolean | null
    hasPreviousPage?: boolean | null
  },
  source: PaginationSource,
  initPage: number,
  pageSizeOptions: number[] = ROWS_PER_PAGE_OPTIONS,
  defaultPageSize = pageSizeOptions[0] ?? DEFAULT_PAGE_SIZE
) => {
  const history = useHistory()
  const location = useLocation()

  const cachedPageSize = getPageOnBySource(source)

  const INITIAL_PAGE_SIZE = getInitialDataGridPageSize({
    search: location.search,
    source,
    pageSizeOptions,
    fallback: defaultPageSize,
  })

  const initialPagination = getPaginationFromSearch(location.search, initPage)

  const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE)
  const [page, setPage] = useState(initialPagination.page)
  const [cursor, setCursor] = useState<PaginationCursor>(
    initialPagination.cursor
  )

  const pageChangeToIgnoreRef = useRef<number | null>(null)
  const pageSizeRef = useRef(INITIAL_PAGE_SIZE)

  const [rowCountState, setRowCountState] = useState(totalCount || 0)

  useEffect(() => {
    setRowCountState(prevRowCountState =>
      totalCount !== undefined ? totalCount : prevRowCountState
    )
  }, [totalCount, setRowCountState])

  useEffect(() => {
    if (
      cachedPageSize != null &&
      !isValidPageSize(cachedPageSize, pageSizeOptions)
    ) {
      setLocalStorageValue(cacheKey(source), INITIAL_PAGE_SIZE)
    }
  }, [cachedPageSize, INITIAL_PAGE_SIZE, pageSizeOptions, source])

  useEffect(() => {
    if (
      getRowsPerPageFromSearch(location.search, pageSizeOptions) !== undefined
    ) {
      return
    }

    const searchParams = new URLSearchParams(location.search)

    searchParams.set(QUERY_PARAMS.ROWS_PER_PAGE, String(pageSizeRef.current))

    history.replace({ search: searchParams.toString() })
  }, [history, location.search, pageSizeOptions])

  useEffect(() => {
    const nextPagination = getPaginationFromSearch(location.search, initPage)
    const rowsPerPage = getRowsPerPageFromSearch(
      location.search,
      pageSizeOptions
    )

    if (rowsPerPage !== undefined && rowsPerPage !== pageSizeRef.current) {
      pageSizeRef.current = rowsPerPage
      setPageSize(rowsPerPage)
      setLocalStorageValue(cacheKey(source), rowsPerPage)
    }

    setPage(currentPage =>
      currentPage === nextPagination.page ? currentPage : nextPagination.page
    )

    setCursor(currentCursor =>
      cursorsEqual(currentCursor, nextPagination.cursor)
        ? currentCursor
        : nextPagination.cursor
    )
  }, [location.search, initPage, pageSizeOptions, source])

  const handlePageChange = (newPage: number) => {
    if (newPage === pageChangeToIgnoreRef.current) {
      pageChangeToIgnoreRef.current = null
      return
    }

    pageChangeToIgnoreRef.current = null
    if (loading) return
    const searchParams = new URLSearchParams(location.search)

    searchParams.set(QUERY_PARAMS.ROWS_PER_PAGE, String(pageSize))

    if (newPage === 0) {
      setPage(newPage)
      setCursor({})
      searchParams.delete(QUERY_PARAMS.PAGE)
      searchParams.delete(QUERY_PARAMS.START_CURSOR)
      searchParams.delete(QUERY_PARAMS.END_CURSOR)
      history.push({ search: searchParams.toString() })
      return
    }

    searchParams.set(QUERY_PARAMS.PAGE, String(newPage + 1))

    if (newPage > page) {
      if (!pageInfo.endCursor) return
      setPage(prev => prev + 1)
      setCursor({ after: pageInfo.endCursor })

      searchParams.set(QUERY_PARAMS.START_CURSOR, pageInfo.endCursor)
      searchParams.delete(QUERY_PARAMS.END_CURSOR)
      history.push({ search: searchParams.toString() })
      return
    }
    if (newPage < page) {
      if (!pageInfo.startCursor) return
      setPage(prev => prev - 1)
      setCursor({ before: pageInfo.startCursor })

      searchParams.set(QUERY_PARAMS.END_CURSOR, pageInfo.startCursor)
      searchParams.delete(QUERY_PARAMS.START_CURSOR)
      history.push({ search: searchParams.toString() })
    }
  }

  const _setPageSize = (size: number) => {
    if (
      !isValidPageSize(size, pageSizeOptions) ||
      size === pageSizeRef.current
    ) {
      return
    }

    const searchParams = new URLSearchParams(location.search)
    const lastPage = Math.max(0, Math.ceil(rowCountState / size) - 1)

    pageChangeToIgnoreRef.current = page > lastPage ? lastPage : null

    pageSizeRef.current = size

    setLocalStorageValue(cacheKey(source), size)
    setPageSize(size)
    setPage(0)
    setCursor({})

    searchParams.delete(QUERY_PARAMS.PAGE)
    searchParams.delete(QUERY_PARAMS.START_CURSOR)
    searchParams.delete(QUERY_PARAMS.END_CURSOR)
    searchParams.set(QUERY_PARAMS.ROWS_PER_PAGE, String(size))

    history.replace({ search: searchParams.toString() })
  }

  return {
    pageSize,
    setPageSize: _setPageSize,
    page,
    setPage,
    cursor,
    rowCountState,
    handlePageChange,
  }
}
